import { useState } from 'react';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { getAPIUrl } from '../api/apiUtils';
import { useGlobalFiltersForAPI } from './useGlobalFilters';
import { getGroupTitleById } from '../utils/groupTitles';
import { apiFetch } from "../api/apiClient";

const API_URL = getAPIUrl();

/**
 * Хук для экспорта данных (постов, комментариев и других типов)
 * Поддерживает пагинацию, прогресс и форматы CSV/XLSX
 */
export const useDataExport = () => {
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [abortController, setAbortController] = useState(null);
  const apiFilters = useGlobalFiltersForAPI();

  /**
   * Экспорт данных с пагинацией
   * @param {string} dataType - Тип данных: 'posts', 'comments', или стандартные типы из export.py
   * @param {object} filters - Фильтры для экспорта (по умолчанию используются глобальные)
   * @param {string} format - Формат файла: 'csv' или 'xlsx'
   * @param {object} options - Дополнительные опции
   */
  const exportData = async (dataType, filters = null, format = 'csv', options = {}) => {
    const exportFilters = filters || apiFilters;
    
    if (!dataType) {
      throw new Error('Не указан тип данных для экспорта');
    }

    const { chunkSize = 1000 } = options;

    // Создаём новый AbortController для этого экспорта
    const controller = new AbortController();
    setAbortController(controller);
    setExporting(true);
    setExportProgress({ current: 0, total: 0, percent: 0 });
    
    try {
      // Определяем endpoint в зависимости от типа данных
      let countEndpoint, chunkEndpoint;
      
      if (dataType === 'posts') {
        countEndpoint = `${API_URL}/export/posts/count`;
        chunkEndpoint = `${API_URL}/export/posts/chunk`;
      } else if (dataType === 'comments') {
        countEndpoint = `${API_URL}/export/comments/count`;
        chunkEndpoint = `${API_URL}/export/comments/chunk`;
      } else if (dataType === 'influencers') {
        countEndpoint = `${API_URL}/export/influencers/count`;
        chunkEndpoint = `${API_URL}/export/influencers/chunk`;
      } else if (dataType === 'users_stats') {
        countEndpoint = `${API_URL}/export/users_stats/count`;
        chunkEndpoint = `${API_URL}/export/users_stats/chunk`;
      } else {
        // Для стандартных типов из export.py (text_markers, topics_intensity, text_anomality)
        countEndpoint = `${API_URL}/export/${dataType}/count`;
        chunkEndpoint = `${API_URL}/export/${dataType}/chunk`;
      }

      // 1. Получаем общее количество записей
      const countResponse = await apiFetch(countEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters: exportFilters }),
        signal: controller.signal
      });
      
      if (!countResponse.ok) {
        throw new Error(`Ошибка получения количества записей: ${countResponse.status}`);
      }
      
      const { total } = await countResponse.json();
      
      if (total === 0) {
        throw new Error('Нет данных для экспорта');
      }
      
      console.log(`Всего записей для экспорта: ${total}`);
      setExportProgress({ current: 0, total, percent: 0 });
      
      // 2. Загружаем данные порциями
      let allData = [];
      let columns = null;
      
      for (let offset = 0; offset < total; offset += chunkSize) {
        const chunkResponse = await apiFetch(chunkEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filters: exportFilters,
            offset,
            limit: chunkSize
          }),
          signal: controller.signal
        });
        
        if (!chunkResponse.ok) {
          throw new Error(`Ошибка загрузки данных: ${chunkResponse.status}`);
        }
        
        const chunk = await chunkResponse.json();
        
        if (!columns && chunk.columns) {
          columns = chunk.columns;
        }
        
        if (chunk.data && chunk.data.length > 0) {
          allData = allData.concat(chunk.data);
        }
        
        // Обновляем прогресс
        const current = Math.min(offset + chunkSize, total);
        const percent = Math.round((current / total) * 100);
        setExportProgress({ current, total, percent });
        
        console.log(`Загружено: ${current} из ${total} (${percent}%)`);
      }
      
      // 3. Формируем имя файла
      const filename = generateFilename(dataType, exportFilters);
      
      // 4. Экспорт в выбранный формат
      if (format === 'csv') {
        exportToCSV(allData, filename);
      } else if (format === 'xlsx') {
        exportToXLSX(allData, filename, dataType);
      }
      
      console.log(`Экспорт завершён: ${allData.length} записей`);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Экспорт отменён пользователем');
        throw new Error('Экспорт отменён');
      }
      throw error;
    } finally {
      setExporting(false);
      setExportProgress({ current: 0, total: 0, percent: 0 });
      setAbortController(null);
    }
  };

  /**
   * Отмена текущего экспорта
   */
  const cancelExport = () => {
    if (abortController) {
      console.log('Отмена экспорта...');
      abortController.abort();
      setExporting(false);
      setExportProgress({ current: 0, total: 0, percent: 0 });
      setAbortController(null);
    }
  };

  /**
   * Генерация имени файла на основе типа данных и фильтров
   */
  const generateFilename = (dataType, filters) => {
    const parts = [];
    
    // Название типа данных
    const typeNames = {
      'posts': 'Посты',
      'comments': 'Комментарии',
      'influencers': 'Инфлюенсеры',
      'text_markers': 'Маркеры',
      'topics_intensity': 'Темы',
      'text_anomality': 'Аномалии',
      'users_stats': 'Статистика пользователей'
    };
    parts.push(typeNames[dataType] || dataType);
    
    // Названия групп (берем из памяти - используем русские названия согласно памяти)
    if (filters.selectedGroups && filters.selectedGroups.length > 0) {
      const groupNames = filters.selectedGroups
        .map(id => getGroupTitleById(id))
        .filter(name => name)
        .join('_');
      if (groupNames) {
        parts.push(groupNames);
      }
    }
    
    // Даты
    if (filters.startDate) {
      parts.push(filters.startDate);
    }
    if (filters.endDate) {
      parts.push(filters.endDate);
    }
    
    return parts.join('_');
  };

  /**
   * Экспорт в CSV формат
   */
  const exportToCSV = (data, filename) => {
    const csv = Papa.unparse(data);
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${filename}.csv`);
  };

  /**
   * Экспорт в XLSX формат
   */
  const exportToXLSX = (data, filename, sheetName = 'Данные') => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    
    // Автоподбор ширины колонок
    const colWidths = [];
    if (data.length > 0) {
      Object.keys(data[0]).forEach((key, i) => {
        const maxLength = Math.max(
          key.length,
          ...data.slice(0, 100).map(row => {
            const val = row[key];
            return val ? String(val).length : 0;
          })
        );
        colWidths[i] = { wch: Math.min(maxLength + 2, 50) };
      });
      ws['!cols'] = colWidths;
    }
    
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  return {
    exportData,
    exporting,
    exportProgress,
    cancelExport
  };
};

